/**
 * HaulerCentral Auth API Service
 *
 * API client for HaulerCentral authentication endpoints.
 * Handles login, registration, password reset, and token management.
 * Uses token interceptor for automatic token refresh on 401 responses.
 * Following patterns from @commander/auth.
 */

import { AUTH_ENDPOINTS, AUTH_STORAGE_KEYS } from '../constants/authConstants';
import * as tokenProvider from '../lib/tokenProvider';
import type {
  ApiResponse,
  AuthResponse,
  ForgotPasswordPayload,
  LoginPayload,
  MeResponse,
  OAuthLinkTokenData,
  OAuthProvider,
  RefreshTokenResponse,
  RegisterPayload,
  ResetPasswordPayload,
  TwoFactorStatusResponse,
  UpdateProfilePayload,
  VerifyEmailPayload,
} from '../types/auth.types';
import { authRequest, publicRequest } from './tokenInterceptor';

/**
 * User data storage key (tokens are managed by tokenProvider)
 */
const USER_KEY = 'haulercentral_user';

function getRequiredApiBaseUrl(): string {
  const configured = import.meta.env?.VITE_API_BASE_URL;
  if (typeof configured !== 'string' || configured.trim().length === 0) {
    throw new Error('[HaulerCentral Auth] Missing required VITE_API_BASE_URL for OAuth redirects');
  }
  return configured.replace(/\/+$/, '');
}

/**
 * Supports both API response envelopes:
 * 1) { success, data: { user, accessToken, refreshToken } }
 * 2) { user, accessToken, refreshToken }
 */
function extractAuthPayload(response: any): {
  user?: unknown;
  accessToken?: string;
  refreshToken?: string;
} {
  const dataEnvelope =
    response && typeof response === 'object' && response.data && typeof response.data === 'object'
      ? response.data
      : response;

  return {
    user: dataEnvelope?.user,
    accessToken: dataEnvelope?.accessToken,
    refreshToken: dataEnvelope?.refreshToken,
  };
}

// ============================================
// Token Management (delegates to tokenProvider)
// ============================================

/**
 * Store auth tokens and user data
 */
export function storeAuth(
  tokens: { accessToken: string; refreshToken: string },
  user: unknown
): void {
  // Store tokens via tokenProvider
  tokenProvider.setTokens(tokens);
  // Store user data in localStorage
  localStorage.setItem(USER_KEY, JSON.stringify(user));

  // Dispatch login event
  window.dispatchEvent(new CustomEvent('auth:login', { detail: { user } }));
}

/**
 * Clear auth tokens and user data
 */
export function clearAuth(): void {
  // Clear tokens via tokenProvider
  tokenProvider.clearAuth();
  // Dispatch logout event
  window.dispatchEvent(new CustomEvent('auth:logout'));
}

/**
 * Get stored access token
 */
export function getAccessToken(): string | null {
  return tokenProvider.getAccessToken();
}

/**
 * Get stored user data
 */
export function getStoredUser(): unknown | null {
  const userStr = localStorage.getItem(USER_KEY);
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return tokenProvider.isAuthenticated();
}

// ============================================
// Auth API Methods
// ============================================

/**
 * Register a new HaulerCentral user
 */
export async function register(payload: RegisterPayload): Promise<AuthResponse> {
  const requestBody = {
    email: payload.email,
    password: payload.password,
    first_name: payload.firstName,
    last_name: payload.lastName,
    phone: payload.phone,
  };

  const response = await publicRequest<AuthResponse>(AUTH_ENDPOINTS.REGISTER, 'POST', requestBody);

  // Store tokens from response body
  if (response.success && response.data?.user) {
    if (response.data.accessToken && response.data.refreshToken) {
      storeAuth(
        {
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
        },
        response.data.user
      );
    }
  }

  return response;
}

/**
 * Login with email and password
 */
export async function login(payload: LoginPayload): Promise<AuthResponse> {
  const response = await publicRequest<AuthResponse>(AUTH_ENDPOINTS.LOGIN, 'POST', payload);

  // Store tokens from response body
  if (response.success && response.data?.user) {
    // Check if tokens are present (not 2FA required)
    if (response.data.accessToken && response.data.refreshToken) {
      storeAuth(
        {
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
        },
        response.data.user
      );
    } else {
      // 2FA required - tokens not provided yet
      localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
    }
  }

  return response;
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
  try {
    await authRequest(AUTH_ENDPOINTS.LOGOUT, 'POST');
  } finally {
    // Always clear local storage, even if API call fails
    clearAuth();
  }
}

/**
 * Get current authenticated user
 */
export async function getCurrentUser(): Promise<MeResponse> {
  return authRequest<MeResponse>(AUTH_ENDPOINTS.ME, 'GET');
}

/**
 * Update user profile
 */
export async function updateProfile(payload: UpdateProfilePayload): Promise<ApiResponse> {
  return authRequest<ApiResponse>(AUTH_ENDPOINTS.UPDATE_PROFILE, 'PUT', payload);
}

/**
 * Request password reset email
 */
export async function forgotPassword(payload: ForgotPasswordPayload): Promise<ApiResponse> {
  return publicRequest<ApiResponse>(AUTH_ENDPOINTS.FORGOT_PASSWORD, 'POST', payload);
}

/**
 * Reset password with token
 */
export async function resetPassword(payload: ResetPasswordPayload): Promise<ApiResponse> {
  return publicRequest<ApiResponse>(AUTH_ENDPOINTS.RESET_PASSWORD, 'POST', payload);
}

/**
 * Verify email with token
 */
export async function verifyEmail(payload: VerifyEmailPayload): Promise<ApiResponse> {
  const response = await publicRequest<any>(AUTH_ENDPOINTS.VERIFY_EMAIL, 'POST', payload);
  const authPayload = extractAuthPayload(response);

  if (authPayload.user && authPayload.accessToken && authPayload.refreshToken) {
    storeAuth(
      {
        accessToken: authPayload.accessToken,
        refreshToken: authPayload.refreshToken,
      },
      authPayload.user
    );
  }

  return response as ApiResponse;
}

/**
 * Resend verification email
 */
export async function resendVerificationEmail(email: string): Promise<ApiResponse> {
  return publicRequest<ApiResponse>(AUTH_ENDPOINTS.RESEND_VERIFICATION, 'POST', { email });
}

// ============================================
// 2FA (Two-Factor Authentication) Methods
// ============================================

/**
 * Get 2FA status for current user
 */
export async function get2FAStatus(): Promise<TwoFactorStatusResponse> {
  return authRequest<TwoFactorStatusResponse>(AUTH_ENDPOINTS.TWO_FACTOR_STATUS, 'GET');
}

/**
 * Enable 2FA - sends verification code to phone
 */
export async function enable2FA(): Promise<ApiResponse> {
  return authRequest<ApiResponse>(AUTH_ENDPOINTS.ENABLE_2FA, 'POST');
}

/**
 * Confirm enable 2FA with verification code
 */
export async function confirmEnable2FA(code: string): Promise<ApiResponse> {
  return authRequest<ApiResponse>(AUTH_ENDPOINTS.CONFIRM_ENABLE_2FA, 'POST', { code });
}

/**
 * Disable 2FA
 */
export async function disable2FA(): Promise<ApiResponse> {
  return authRequest<ApiResponse>(AUTH_ENDPOINTS.DISABLE_2FA, 'POST');
}

/**
 * Send 2FA code during login
 */
export async function send2FACode(userId: string): Promise<ApiResponse> {
  return publicRequest<ApiResponse>(AUTH_ENDPOINTS.SEND_2FA_CODE, 'POST', { u: userId });
}

/**
 * Verify 2FA code and complete login
 */
export async function verify2FA(userId: string, code: string): Promise<AuthResponse> {
  const response = await publicRequest<AuthResponse>(AUTH_ENDPOINTS.VERIFY_2FA, 'POST', {
    u: userId,
    code,
  });

  // Store tokens from response body
  if (response.success && response.data?.user) {
    if (response.data.accessToken && response.data.refreshToken) {
      storeAuth(
        {
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
        },
        response.data.user
      );
    }
  }

  return response;
}

/**
 * Refresh access token using refresh token
 */
export async function refreshToken(token: string): Promise<RefreshTokenResponse> {
  const response = await publicRequest<RefreshTokenResponse>(AUTH_ENDPOINTS.REFRESH, 'POST', {
    refresh_token: token,
  });

  // Store new tokens from response body
  if (response.success && response.data?.accessToken) {
    storeAuth(
      {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken || token,
      },
      getStoredUser()
    );
  }

  return response;
}

// ============================================
// OAuth Methods
// ============================================

/**
 * Initiate OAuth login with provider
 */
export function initiateOAuth(provider: OAuthProvider, returnTo?: string): void {
  const API_BASE_URL = getRequiredApiBaseUrl();
  const state = JSON.stringify({ origin: 'haulercentral', returnTo });
  const oauthUrl = `${API_BASE_URL}${AUTH_ENDPOINTS.OAUTH_BASE}/${provider}?state=${encodeURIComponent(
    state
  )}`;
  window.location.href = oauthUrl;
}

/**
 * Handle OAuth callback
 * Tokens are now stored in httpOnly secure cookies by the backend.
 * This function verifies the auth status from URL params and extracts
 * the tokens from localStorage (which are populated by axios interceptors
 * that read from the secure cookies).
 *
 * P1 Security Fix: Removed token extraction from URL params to prevent
 * credential exposure in browser history, proxy logs, and Referer headers.
 */
export function handleOAuthCallback(): { accessToken: string; refreshToken: string } | null {
  const params = new URLSearchParams(window.location.search);
  const authStatus = params.get('auth');

  // Check if OAuth authentication was successful
  if (authStatus === 'success') {
    // Tokens are stored in secure httpOnly cookies by backend
    // They will be sent automatically by axios via cookie-based auth
    // or can be accessed from localStorage if they were previously stored
    const access_token = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    const refresh_token = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);

    if (access_token && refresh_token) {
      return { accessToken: access_token, refreshToken: refresh_token };
    }

    // Tokens in cookies will be used for subsequent requests
    // Return null to indicate tokens are in secure cookies
    return null;
  }

  return null;
}

/**
 * Link OAuth account to existing user
 */
export async function linkOAuthAccount(
  provider: OAuthProvider,
  tokenData: OAuthLinkTokenData
): Promise<ApiResponse> {
  return authRequest<ApiResponse>(AUTH_ENDPOINTS.OAUTH_LINK, 'POST', {
    provider,
    ...tokenData,
  });
}

/**
 * Unlink OAuth account
 */
export async function unlinkOAuthAccount(provider: OAuthProvider): Promise<ApiResponse> {
  return authRequest<ApiResponse>(`${AUTH_ENDPOINTS.OAUTH_UNLINK}/${provider}`, 'DELETE');
}

// ============================================
// Auth Helpers
// ============================================

/**
 * Attempt to refresh token if expired
 */
export async function tryRefreshToken(): Promise<boolean> {
  const token = tokenProvider.getRefreshToken();
  if (!token) {
    return false;
  }

  try {
    await refreshToken(token);
    return true;
  } catch {
    clearAuth();
    return false;
  }
}

export default {
  // Token management
  storeAuth,
  clearAuth,
  getAccessToken,
  getStoredUser,
  isAuthenticated,
  // Auth methods
  register,
  login,
  logout,
  getCurrentUser,
  updateProfile,
  forgotPassword,
  resetPassword,
  verifyEmail,
  resendVerificationEmail,
  refreshToken,
  // 2FA methods
  get2FAStatus,
  enable2FA,
  confirmEnable2FA,
  disable2FA,
  send2FACode,
  verify2FA,
  // OAuth methods
  initiateOAuth,
  handleOAuthCallback,
  linkOAuthAccount,
  unlinkOAuthAccount,
  // Helpers
  tryRefreshToken,
};
